'use client';

import type { ClassValue } from 'clsx';
import type { GalleryImageT } from '../../../../../type/galleryImageT';
import type { GalleryVideoT } from '../../../../../type/galleryVideo';

import { useArrowKeyNavigation } from '@vs-shop/headless/useArrowKeyNavigation';
import { useGalleryState } from '@vs-shop/headless/useGalleryState';
import type { ProductLabel } from '../../../../../product/index';
import {
  ProductGallery as ProductGallerySection,
  ProductGalleryAgeBadge,
  ProductGalleryDiscountBadge,
  ProductGalleryLabels,
  ProductGalleryPlaceholder,
  ProductGalleryVariant,
  ProductGalleryViewport,
} from '@vs-shop/commerce-ui/productGallery';
import { ProductLabelVariant } from '@vs-shop/commerce-ui/productLabel';
import { IconAgeRestriction } from '@vs-shop/ui/icons';
import { ImageMissingPlaceholder } from '@vs-shop/ui/imageMissingPlaceholder';
import { clsxm } from '@vs-shop/utils';
import { useMemo } from 'react';

import { age18 } from '../../../../../config/checkout';

import GalleryNextStandaloneButton from './GalleryNextStandaloneButton';
import GalleryPrevStandaloneButton from './GalleryPrevStandaloneButton';
import { GalleryDiscountBadge } from '../../../../server/page/product/gallery/GalleryDiscountBadge';
import { GalleryMainImageDesktop } from '../../../../server/page/product/gallery/GalleryMainImageDesktop';
import { GalleryMainImageMobile } from '../../../../server/page/product/gallery/GalleryMainImageMobile';
import { GalleryModal } from '../../../../server/page/product/gallery/GalleryModal';
import { GalleryThumbBar } from '../../../../server/page/product/gallery/GalleryThumbBar';
import { ProductLabels } from '../../../../server/page/product/gallery/ProductLabels';
import { galleryLabels } from '../../../../server/page/product/gallery/productLabelPlacement';

import type { ProductBaseIndex } from '../../../../../effect/schema/openapi/backend';
import {
  calculateShownThumbs,
  combineMedia,
  findFirstVideoIndex,
  hasVideos as checkHasVideos,
  sortMediaByPosition,
} from '../../../../../effect/utils/gallery/media';

type Props = {
  images: GalleryImageT[] | null;
  videos: GalleryVideoT[] | null;
  productName: string;
  alt: string;
  discountPercentage: number | null;
  labels: readonly ProductLabel[];
  ageRestriction: ProductBaseIndex['ageRestriction'];
  className?: ClassValue;
};

/**
 * rcprofi's arrangement of the shared product gallery, and the state it runs on.
 *
 * The single-frame stage and the swipe strip are both composed, each hidden at
 * the width the other serves — where that switch falls is this tenant's layout
 * decision, not the section's. What hangs over the imagery is this tenant's too:
 * its discount medallion, its age mark, its label set.
 */
export default function ProductGallery({
  images,
  videos,
  productName,
  alt,
  labels,
  discountPercentage,
  ageRestriction,
  className,
}: Props) {
  const {
    selectedIdx,
    modalOpen,
    modalSelectedIdx,
    setSelectedIdx,
    setModalSelectedIdx,
    handleShowModal,
    handleCloseModal,
    goToPrev,
    goToNext,
  } = useGalleryState();

  const media = useMemo(() => combineMedia(images, videos), [images, videos]);
  const mediaSortedByPosition = useMemo(() => sortMediaByPosition(media), [media]);
  const hasVideoMedia = useMemo(() => checkHasVideos(videos), [videos]);
  const shownThumbs = useMemo(() => calculateShownThumbs(hasVideoMedia), [hasVideoMedia]);
  const firstVideoIndex = useMemo(
    () => findFirstVideoIndex(mediaSortedByPosition),
    [mediaSortedByPosition]
  );

  const selectedMedia = mediaSortedByPosition[selectedIdx];
  const maxIdx = mediaSortedByPosition.length - 1;
  const isVideoActive = selectedMedia?.mediaType === 'video';

  useArrowKeyNavigation({
    enabled: !modalOpen,
    selectedIdx,
    setSelectedIdx,
    maxIdx,
  });

  return (
    <ProductGallerySection className={clsxm(className)}>
      <ProductGalleryViewport>
        {!selectedMedia ? (
          <ProductGalleryPlaceholder variant={ProductGalleryVariant.INLINE}>
            <ImageMissingPlaceholder />
          </ProductGalleryPlaceholder>
        ) : (
          <>
            <GalleryMainImageDesktop
              selectedMedia={selectedMedia}
              onOpen={() => {
                handleShowModal(selectedIdx);
              }}
              alt={alt}
              className="hidden lap:block"
              isActive={isVideoActive}
            />
            <GalleryMainImageMobile
              media={mediaSortedByPosition}
              onOpen={handleShowModal}
              selectedIdx={selectedIdx}
              setSelectedIdx={setSelectedIdx}
              alt={alt}
              className="lap:hidden"
            />

            <GalleryPrevStandaloneButton
              onPrev={() => {
                goToPrev(maxIdx);
              }}
              disabled={false}
            />

            <GalleryNextStandaloneButton
              onNext={() => {
                goToNext(maxIdx);
              }}
              disabled={false}
            />

            {discountPercentage !== null && (
              <ProductGalleryDiscountBadge>
                <GalleryDiscountBadge discountPercentage={discountPercentage} />
              </ProductGalleryDiscountBadge>
            )}
            {ageRestriction === age18 && (
              <ProductGalleryAgeBadge>
                <IconAgeRestriction className="size-[60px] lap:size-[80px]" />
              </ProductGalleryAgeBadge>
            )}
            {labels.length > 0 && (
              <ProductGalleryLabels>
                <ProductLabels labels={galleryLabels(labels)} variant={ProductLabelVariant.ROOMY} />
              </ProductGalleryLabels>
            )}
          </>
        )}
      </ProductGalleryViewport>

      {selectedMedia && (
        <>
          <GalleryThumbBar
            images={images ?? []}
            videos={videos ?? []}
            mediaSortedByPosition={mediaSortedByPosition}
            selectedIdx={selectedIdx}
            firstVideoIndex={firstVideoIndex}
            onThumb={(idx) => {
              setSelectedIdx(idx);
            }}
            onShowAll={() => {
              handleShowModal(0);
            }}
            onShowVideo={handleShowModal}
            mediaShown={shownThumbs}
            alt={alt}
          />
          <GalleryModal
            open={modalOpen}
            mediaSortedByPosition={mediaSortedByPosition}
            selectedIdx={modalSelectedIdx}
            setSelectedIdx={setModalSelectedIdx}
            onClose={handleCloseModal}
            productName={productName}
            alt={alt}
          />
        </>
      )}
    </ProductGallerySection>
  );
}
